import axios from "axios";
import type {SessionContext} from "./SessionContext";
// The query-string parameters a source application appends during a single-sign-on hand-off.
export const SSO_ACCESS_TOKEN_PARAM = "ssoAccessToken";

// PROPOSED, NOT IMPOSED. One string, `<accountUSID>:<Mode>:<USID>` -- exactly the `Visibility` the
// exchange already accepts, so nothing has to be reassembled at the other end.
//
// THE PERSONA USED TO CROSS AND WAS REMOVED, correctly as it was then done: the target ADOPTED it
// blindly, and a persona is a Position, User Group or Work Group whose USID means nothing outside
// the structure it came from. What crossed was a USID from somewhere else.
//
// WHAT MAKES CARRYING IT SOUND NOW IS THAT NOBODY TRUSTS IT. The exchange verifies the persona
// against the list it would itself have offered and refuses with CC-5OUE-S3RG when it does not
// belong -- a guard that was already there -- and the caller falls back to asking. So the hint can
// only ever save a question it would have had to ask anyway; it can never answer one wrongly.
//
// THE ACCOUNT HALF IS DIFFERENT, AND SAFE OUTRIGHT. The suite is one database partitioned by realm:
// an account is the same row in every app, so an account USID means the same thing on both sides of
// the hand-off. It is the persona that is app-shaped, because a Position carries a FOCUS_AREA and
// QMS offers only its own.
export const SSO_VISIBILITY_PARAM = "ssoVisibility";

// Builds the URL a source application opens when handing off to a target app: when signed in, the
// current access token is appended so the target can exchange it for a native session, plus the
// account and persona as a hint the target verifies before using. Without a token the URL is
// returned unchanged.
/** Whether this account actually holds the persona behind a visibility value.
 *
 *  SWITCHING ACCOUNT IS PRECISELY WHEN A PERSONA STOPS BEING HELD, so the value carried over from
 *  the account being left has to be checked against the one being entered. Adopted blindly, every
 *  read comes back refused CC-PRSN-DENY at 403 and the app looks broken rather than wrongly
 *  scoped. */
export function accountHoldsPersona(account: any, value?: string | null): boolean {
    if (!account || !value) return false;
    const groups = [account["Positions"], account["UserGroups"], account["WorkGroups"]];
    return groups.some(group => Array.isArray(group)
        && group.some((one: any) => one && String(one.USID) === String(value)));
}


export function buildSsoHandoffUrl(
    externalUrl: string,
    accessToken: string | null,
    visibility?: string | null,
): string {
    if (!accessToken) {
        return externalUrl;
    }
    const separator = externalUrl.includes("?") ? "&" : "?";
    let url = `${externalUrl}${separator}${SSO_ACCESS_TOKEN_PARAM}=${encodeURIComponent(accessToken)}`;

    // ALL THREE PARTS OR NONE. A half-formed visibility is not a narrower hint, it is an
    // unresolvable one, and the exchange would split it into pieces that name nothing.
    if (visibility && visibility.split(":").filter(Boolean).length === 3) {
        url += `&${SSO_VISIBILITY_PARAM}=${encodeURIComponent(visibility)}`;
    }
    return url;
}


// The hint a source application offers, or null when it cannot form a whole one.
//
// ONE PLACE, because the three parts come from two different session accessors and a call site that
// assembled them by hand got it wrong once already: ParkourDocuments passed the raw
// `{Mode, Value}` object straight through, which produced a URL parameter reading
// "[object Object]" rather than a visibility.
export function handoffVisibility(sessionContext: any): string | null {
    const accountUsid = sessionContext?.getAccount?.()?.["AccountUSID"];
    const persona = sessionContext?.getActiveVisibility?.();
    if (!accountUsid || !persona?.Mode || !persona?.Value) {
        return null;
    }
    return `${accountUsid}:${persona.Mode}:${persona.Value}`;
}

// A selectable "persona" = a Visibility {Mode, Value}. Flattens an Account's Positions / UserGroups /
// WorkGroups into the list the persona-selection prompt offers.
//
// It used to carry a `family` too, so a persona passed from another app could be matched against this
// one's list across the several Modes that share a Value. Nothing matches across apps any more, so the
// field went with the matching rather than lingering as a hint that some resolution still happens.
export type PersonaOption = {mode: string, value: string, name: string};

export function personaOptions(account: any): PersonaOption[] {
    if (!account) {
        return [];
    }
    const options: PersonaOption[] = [];
    for (const position of account["Positions"] ?? []) {
        options.push({mode: "Position", value: position.USID, name: position.Name});
    }
    for (const userGroup of account["UserGroups"] ?? []) {
        options.push({mode: "UserGroup", value: userGroup.USID, name: userGroup.Name});
    }
    for (const workGroup of account["WorkGroups"] ?? []) {
        options.push({mode: "WorkGroup", value: workGroup.USID, name: workGroup.Name});
    }
    return options;
}

// Exchanges a foreign suite access token for a native access token via this
// tenant's sso_authenticate endpoint, then establishes the local session using
// the same response shape that authenticate returns.
//
// NO PERSONA IS CHOSEN HERE, and that is the point. This used to honor one passed by the source, and
// failing that auto-select when the user had exactly one. Both are gone: the personas of one app are
// not the personas of another and never will be, so a persona arriving from the source was a USID
// from a different account structure, and auto-selecting the target's first one merely guessed
// quietly instead of loudly.
//
// The visibility is left unset and useSsoLogin prompts, which is the one place the answer exists.
// Note this is deliberately NOT what a direct login does -- it picks the first Position without
// asking. Arriving from another app is the case where the user has demonstrably just been working as
// somebody in a different structure, so it is the case where guessing is least defensible.
//
// WHAT WENT WRONG IS PART OF THE ANSWER, so this no longer returns a bare boolean.
//
// It used to say only "established, or not", and the screen that renders the "not" told the user
// their link had expired. That is right for a 401 and wrong for a 500, and the suite spent a while
// in the second case while everybody read the first message: the authorizer refused every
// cross-app transfer, the exchange answered 500, and the user was told to sign in again.
//
// A fault is a 5xx or no response at all (a network failure, CORS, a timeout). Everything else --
// 401, 403, a 200 whose body is missing the token -- is a link that will not work and a sign-in
// that will.
// THE THIRD OUTCOME IS NOT A FAILURE. An email that belongs to more than one account gets a 200
// carrying `{Code: "CC-ADC7-49A2", Accounts: {...}}` -- the same contract authenticate uses, asking
// the frontend to pick and re-submit with an explicit Visibility. CC-E5EB-43D5 is the same door for
// one account with several personas. Treating either as a failure is what told a person in two
// tenants that their sign-in link had expired, on a 200, every time.
export const SSO_CHOOSER_CODES = ["CC-ADC7-49A2", "CC-E5EB-43D5"];

export type SsoResult =
    | {established: true}
    | {established: false; chooser: Record<string, any>}
    | {established: false; fault: boolean; code?: string | null};

export async function ssoAuthenticate(
    foreignAccessToken: string,
    sessionContext: SessionContext,
    // `<accountUSID>:<Mode>:<USID>` on the re-submit after the chooser; absent on the first call.
    visibility?: string,
): Promise<SsoResult> {
    const response: any = await axios({
        method: "POST",
        baseURL: process.env.REACT_APP_API_URL,
        url: "t13/v1/sso_authenticate",
        headers: {
            // The foreign token is the credential; the backend validates it and
            // mints a native token in return.
            "X-Cres-AccessToken": foreignAccessToken,
        },
        data: visibility ? {Visibility: visibility} : {},
    }).catch((e: any) => e.response || e);

    // No response object at all means the request never completed -- ours to answer for, not a
    // dead link the user can do anything about.
    if (response instanceof Error || typeof response.status !== "number") {
        return {established: false, fault: true, code: null};
    }
    if (response.status !== 200) {
        return {
            established: false,
            fault: response.status >= 500,
            code: response.data?.Code ?? null,
        };
    }

    const data = response.data;
    if (data && SSO_CHOOSER_CODES.includes(data.Code)) {
        return {established: false, chooser: data["Accounts"] ?? {}};
    }
    if (!data || !data["AccessToken"] || !data["Account"]) {
        // A 200 that cannot be used is a fault here, not an expired link: the server said yes.
        return {established: false, fault: true, code: data?.Code ?? null};
    }


    sessionContext.setIdentityToken(data["IdentityToken"]);
    sessionContext.setSessionUSID(data["Session"]);
    sessionContext.setAccessToken(data["AccessToken"]);
    sessionContext.setRefreshToken(data["RefreshToken"]);
    sessionContext.setAccountName(data["Account"]["AccountName"]);
    sessionContext.setAccountUserUSID(data["Account"]["AccountUserUSID"]);
    sessionContext.setAccount(data["Account"]);
    sessionContext.setRoles(data["Roles"]);
    sessionContext.setPrivileges(data.Privileges);
    sessionContext.setProducts(data.Products || []);
    sessionContext.setPrivilegeConstraints(data.PrivilegeConstraints);
    sessionContext.setTenants(data["Tenants"]);
    sessionContext.setShowLoginDialog(false);
    sessionContext.setPickValues(data["PickValues"]);

    if (visibility) {
        // THE ONE CASE THAT DOES SET A PERSONA. Everything above deliberately leaves visibility
        // unset so useSsoLogin can prompt; here the user has just chosen, in the chooser, and
        // prompting again for what they picked a second ago is noise.
        const [, mode, value] = visibility.split(":");
        if (mode && value) sessionContext.setActiveVisibility(mode, value);
    }

    return {established: true};
}
